import type { HTMLAttributes, ReactNode } from 'react';

export type CardSize = 'form' | 'page';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  /**
   * UX-03 — 'form' (défaut, comportement INCHANGÉ depuis FND-20) : boîte
   * étroite centrée, pour un flux à un seul champ/une seule action
   * (Login/Register/ForgotPassword/ResetPassword/CreateSchool/AccessDenied/
   * Home) — `max-w-md`. 'page' : PAS de largeur maximale imposée (`w-full`
   * seul) — pour les pages de gestion au contenu plus large (listes,
   * formulaires multi-champs, récapitulatifs — Students/Fees/ManageClasses/
   * SchoolYears/Payments/CombinedPayments/SupplyOrderPayments/SupplyCatalog/
   * Enrollments) ET pour un usage en GRILLE (UX-04, plusieurs `Card` côte à
   * côte) : une largeur maximale fixe fixée par le composant lui-même
   * entrerait en conflit avec la largeur imposée par la cellule de grille
   * parente — voir docs/architecture.md, section "UX-03", pour le détail
   * complet de cette décision et sa vérification.
   */
  size?: CardSize;
  /**
   * Niveau de titre rendu pour `title` (audit Impeccable, 2026-09-07).
   *
   * PROBLÈME RÉEL corrigé : `Card` rendait systématiquement un `<h3>`, et
   * comme c'est le point d'entrée visuel de quasiment toute page, les deux
   * apps ne comptaient que **2 `<h1>` pour ~54 pages**. Chaque écran démarrait
   * donc au niveau 3, sans niveau 1 ni 2. Un lecteur d'écran ne pouvait ni
   * annoncer la page ni naviguer par titres — la première aide de navigation
   * sur un outil de gestion à 54 écrans (WCAG 2.1 AA, 1.3.1 et 2.4.6).
   *
   * Défaut `h1` et non `h3` : les 113 usages de `<Card title>` du dépôt sont
   * TOUS le titre de leur page (vérifié un par un avant de changer le défaut ;
   * les pages qui rendent plusieurs `Card` titrées le font dans des branches
   * EXCLUSIVES — chargement, erreur, accès refusé, contenu — donc une seule
   * est montée à la fois). Un défaut `h3` conservé « par prudence » aurait
   * laissé les 113 appelants à corriger un par un, c'est-à-dire jamais.
   *
   * `titleAs` reste disponible pour le cas légitime où une `Card` est
   * imbriquée dans une page qui porte déjà son `<h1>`.
   *
   * Purement SÉMANTIQUE : l'apparence ne bouge pas d'un pixel, les classes
   * Tailwind du titre étant identiques quel que soit le niveau (un `<h1>` non
   * stylé serait sinon rendu en 2em gras par la feuille de style par défaut du
   * navigateur — ce que Tailwind neutralise déjà via son preflight).
   */
  titleAs?: 'h1' | 'h2' | 'h3';

  children: ReactNode;
}

const SIZE_STYLES: Record<CardSize, string> = {
  form: 'ss-card--form w-full max-w-md',
  page: 'ss-card--page w-full',
};

// FND-20, étendu par UX-03 : conteneur "panneau" de premier niveau, utilisé
// comme point d'entrée visuel de quasiment toute page — centré
// verticalement/horizontalement par `<main>` (voir apps/web-admin/src/App.tsx
// et index.css), donc `Card` n'a besoin de se soucier que de son propre
// habillage (largeur, fond blanc, coins arrondis, ombre légère), pas du
// positionnement global.
//
// UX-03 : bordure passée de `border-border-subtle` à `border-border` — socle de
// design cohérent avec `Panel` (voir Panel.tsx, même couleur de bordure) et
// avec `NavBar` (`border-b border-border`, déjà en place depuis UX-01) ;
// `border-border-subtle` était visuellement presque invisible sans zoomer, un vrai
// défaut relevé par l'audit de ce ticket — `shadow-sm` seul ne suffisait pas à
// clarifier la limite du panneau sur un fond `bg-surface-muted` (voir index.css) très
// proche du blanc du panneau lui-même.
export function Card({ title, size = 'form', titleAs: TitleTag = 'h1', className, children, ...rest }: CardProps) {
  // UX-02 : `p-4 sm:p-8` — padding réduit sous 640px (mobile), revient au padding
  // desktop d'origine (`p-8`, INCHANGÉ) dès `sm:` — sur un écran ~375px, `p-8`
  // (32px de chaque côté, en plus du `px-4` de `<main>`, voir App.tsx) laissait
  // trop peu de largeur utile au contenu (tableaux notamment, voir UX-02).
  const classes = ['ss-card', SIZE_STYLES[size], 'rounded-xl border border-border bg-white p-4 sm:p-8 shadow-sm', className]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={classes} {...rest}>
      {/* `ss-card__title` et les classes Tailwind sont IDENTIQUES quel que soit
          le niveau (voir `titleAs`) : ce changement est sémantique, pas visuel.
          Des tests existants s'appuient sur ce marqueur. */}
      {title ? <TitleTag className="ss-card__title mb-6 text-xl font-semibold text-text">{title}</TitleTag> : null}
      <div className="ss-card__body flex flex-col gap-4">{children}</div>
    </div>
  );
}
